import { useEffect, useRef, useState } from 'react';
import { FotoboxRecord } from '../types';

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

interface BoxCardProps {
  box: FotoboxRecord;
  index: number;
  isAdmin: boolean;
}

/** Erkennt den Typ anhand Box-ID oder Name.
 *  FBTN-* = FBT-Fotoboxen
 *  FBTM-* / SB-* / SCH-* / Name enthält "Schick" = Schickboxen
 */
function getBoxBrand(box: FotoboxRecord): 'fbt' | 'schickbox' | null {
  const id = box.id.toUpperCase();
  const name = box.name.toUpperCase();
  // Schickbox zuerst prüfen (spezifischer)
  if (id.startsWith('FBTM') || id.startsWith('SB') || id.startsWith('SCH') || name.includes('SCHICK')) {
    return 'schickbox';
  }
  if (id.startsWith('FBTN') || name.includes('FBT') || name.includes('FOTOBOX TEAM')) {
    return 'fbt';
  }
  return null;
}

function relativeTime(ts: number): string {
  const diff = Date.now() - ts;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `vor ${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `vor ${m} Min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `vor ${h} Std`;
  return `vor ${Math.floor(h / 24)} Tagen`;
}

/** Kein Drucker angeschlossen – neutral behandeln, kein Fehlerzustand */
function isNotConnected(box: FotoboxRecord): boolean {
  if (!box.printer_status || box.printer_status === 'unknown') return true;
  if (box.printer_status === 'error' && box.printer_error === 'Drucker nicht angeschlossen') return true;
  return false;
}

function getCardClass(box: FotoboxRecord): string {
  if (box.status === 'offline') return 'card-offline';
  if (box.printer_status === 'error' && !isNotConnected(box)) return 'card-glow-error';
  if (box.printer_status === 'warning') return 'card-glow-warning';
  if (box.prints_remaining !== null && box.prints_remaining <= 20) return 'card-glow-warning';
  return 'card-glow-online';
}

function getLedClass(box: FotoboxRecord): string {
  if (box.status === 'offline') return 'led-offline';
  if (box.printer_status === 'error' && !isNotConnected(box)) return 'led-error';
  if (box.printer_status === 'warning') return 'led-warning';
  return 'led-online';
}

function PrintsBar({ remaining, rollSize, threshold }: { remaining: number; rollSize: number; threshold: number }) {
  const pct = Math.min(100, Math.round((remaining / rollSize) * 100));

  let color = '#07b295';
  if (remaining <= 10) color = '#E80444';
  else if (remaining <= threshold) color = '#f59e0b';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-1">
        <span className="font-opensans text-ink-muted text-[10px] sm:text-xs flex-shrink-0">Drucke</span>
        <span className="font-mono text-[11px] sm:text-sm font-semibold whitespace-nowrap flex-shrink-0" style={{ color }}>
          {remaining}<span className="text-ink-faint font-normal"> / {rollSize}</span>
        </span>
      </div>
      <div className="print-bar-track">
        <div
          className="print-bar-fill"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <div className="flex justify-between text-[10px] text-ink-faint">
        <span>0</span>
        <span>{Math.round(pct)}%</span>
        <span>{rollSize}</span>
      </div>
    </div>
  );
}

function PrinterStatusBadge({ status, printerError }: { status: FotoboxRecord['printer_status']; printerError?: string | null }) {
  const notConnected =
    !status ||
    status === 'unknown' ||
    (status === 'error' && printerError === 'Drucker nicht angeschlossen');

  if (notConnected) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-surface-subtle text-ink-muted border border-surface-border">
        <span className="w-1.5 h-1.5 rounded-full bg-ink-faint inline-block" />
        <span className="hidden sm:inline">Nicht angeschlossen.</span>
        <span className="sm:hidden">Nicht angeschl.</span>
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-red-light text-brand-red border border-brand-red-mid">
        <span className="w-1.5 h-1.5 rounded-full bg-brand-red inline-block animate-pulse" />
        Fehler
      </span>
    );
  }
  if (status === 'warning') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 border border-amber-200">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block animate-pulse" />
        Warnung
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-brand-green-light text-brand-green border border-brand-green-mid">
      <span className="w-1.5 h-1.5 rounded-full bg-brand-green inline-block" />
      Bereit
    </span>
  );
}

/** Remote-Verbindungs-Icon (Monitor mit Cursor/Pfeil) */
function RemoteIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
      <path d="M9 10l2 2 4-4" />
    </svg>
  );
}

function effectiveAnydesk(box: FotoboxRecord): string | null {
  return box.anydesk_id ?? null;
}

function AnydeskButton({ box, isAdmin }: { box: FotoboxRecord; isAdmin: boolean }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(box.anydesk_id ?? '');
  const [saving, setSaving] = useState(false);
  const [localId, setLocalId] = useState<string | null>(null);

  const effectiveId = localId ?? box.anydesk_id;

  useEffect(() => {
    if (localId && box.anydesk_id === localId) setLocalId(null);
  }, [box.anydesk_id, localId]);

  async function save() {
    const trimmed = value.trim();
    setSaving(true);
    try {
      const token = localStorage.getItem('fb_token');
      const res = await fetch(`${API_BASE}/api/boxes/${encodeURIComponent(box.id)}/anydesk`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ anydesk_id: trimmed || null }),
      });
      if (res.ok) {
        setLocalId(trimmed || null);
        setValue(trimmed);
      }
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  // Nicht-Admins sehen nur den Link (falls ID gesetzt), kein Bearbeiten
  if (!isAdmin && !effectiveId) return null;

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        <input
          autoFocus
          type="text"
          placeholder="AnyDesk-ID"
          value={value}
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          className="flex-1 min-w-0 bg-surface-subtle border border-surface-border-dark rounded-lg px-2.5 py-1 text-xs font-mono text-ink-primary focus:outline-none focus:border-brand-green focus:ring-1 focus:ring-brand-green/30"
        />
        <button
          onClick={save}
          disabled={saving}
          className="px-2.5 py-1 rounded-lg text-xs bg-brand-green hover:bg-brand-green/90 text-white font-medium disabled:opacity-50 transition-colors"
        >
          {saving ? '…' : 'OK'}
        </button>
        <button
          onClick={() => setEditing(false)}
          className="px-2.5 py-1 rounded-lg text-xs bg-surface-subtle hover:bg-surface-border text-ink-secondary border border-surface-border transition-colors"
        >
          ✕
        </button>
      </div>
    );
  }

  if (effectiveId) {
    return (
      <div className="flex items-center gap-1.5 mt-1">
        <a
          href={`anydesk:${effectiveId}`}
          className="flex-1 flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-surface-subtle border border-surface-border text-ink-secondary hover:border-brand-green hover:text-brand-green hover:bg-brand-green-light transition-all text-xs font-medium"
          title={`AnyDesk verbinden: ${effectiveId}`}
        >
          <RemoteIcon className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-opensans">AnyDesk · {effectiveId}</span>
        </a>
        {isAdmin && (
          <button
            onClick={() => { setValue(effectiveId); setEditing(true); }}
            className="p-1.5 rounded-lg text-ink-faint hover:text-ink-secondary hover:bg-surface-subtle border border-transparent hover:border-surface-border transition-all"
            title="AnyDesk-ID bearbeiten"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
        )}
      </div>
    );
  }

  if (!isAdmin) return null;

  return (
    <button
      onClick={() => setEditing(true)}
      className="mt-1 w-full flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-dashed border-surface-border-dark text-ink-faint hover:text-brand-green hover:border-brand-green hover:bg-brand-green-light transition-all text-xs"
    >
      <RemoteIcon className="w-3.5 h-3.5" />
      <span className="font-opensans">AnyDesk-ID hinzufügen</span>
    </button>
  );
}

export default function BoxCard({ box, index, isAdmin }: BoxCardProps) {
  const prevId = useRef<string>(box.id);
  const prevStatus = useRef<string>(box.status);
  const prevPrinterStatus = useRef<string | null>(box.printer_status);
  const [flashing, setFlashing] = useState(false);
  const [, forceUpdate] = useState(0);

  // Relative time ticker
  useEffect(() => {
    const id = setInterval(() => forceUpdate((n) => n + 1), 15_000);
    return () => clearInterval(id);
  }, []);

  // Flash nur bei echten Statusänderungen
  useEffect(() => {
    if (prevId.current !== box.id) {
      prevId.current = box.id;
      prevStatus.current = box.status;
      prevPrinterStatus.current = box.printer_status;
      return;
    }
    const statusChanged = prevStatus.current !== box.status;
    const printerChanged = prevPrinterStatus.current !== box.printer_status;
    if (statusChanged || printerChanged) {
      setFlashing(true);
      const t = setTimeout(() => setFlashing(false), 900);
      prevStatus.current = box.status;
      prevPrinterStatus.current = box.printer_status;
      return () => clearTimeout(t);
    }
    prevStatus.current = box.status;
    prevPrinterStatus.current = box.printer_status;
  }, [box.status, box.printer_status, box.id]);

  const isOffline = box.status === 'offline';
  const brand = getBoxBrand(box);

  return (
    <div
      className={[
        'relative rounded-xl sm:rounded-2xl border bg-surface-card',
        'p-2.5 sm:p-4',
        'flex flex-col gap-2 sm:gap-3',
        'transition-all duration-500 animate-fade-up',
        getCardClass(box),
        flashing ? 'card-flash' : '',
        isOffline ? 'opacity-60' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ animationDelay: `${index * 40}ms` }}
    >
      {/* Top row: Brand-Logo + Name + Status badge */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5 sm:gap-2.5 min-w-0">
          {brand && (
            <img
              src={brand === 'fbt' ? '/fbt.png' : '/schickbox.png'}
              alt={brand === 'fbt' ? 'FBT' : 'Schickbox'}
              className="flex-shrink-0 h-6 sm:h-9 w-auto max-w-[28px] sm:max-w-[40px] object-contain"
            />
          )}
          {!brand && (
            <div className={`flex-shrink-0 w-2 h-2 rounded-full ${getLedClass(box)}`} />
          )}
          <div className="min-w-0">
            {/* Mobile: kompaktes Block mit eng beieinander */}
            <div className="sm:hidden leading-none">
              <div className="font-raleway font-semibold text-[11px] uppercase tracking-[0.08em] text-ink-primary">
                {brand === 'fbt' ? 'FBT' : brand === 'schickbox' ? 'Schickbox' : box.name}
              </div>
              <div className="font-opensans text-[9px] text-ink-primary font-medium mt-[2px]">{box.id}</div>
            </div>
            {/* Desktop: voller Name + ID */}
            <div className="hidden sm:block">
              <h2 className="font-raleway font-semibold text-sm uppercase tracking-[0.1em] text-ink-primary truncate leading-tight">
                {box.name}
              </h2>
              <p className="font-opensans text-[10px] text-ink-faint mt-0.5">{box.id}</p>
            </div>
          </div>
        </div>

        {/* LED + Status badge */}
        <div className="flex items-center gap-1 flex-shrink-0">
          {brand && <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full flex-shrink-0 ${getLedClass(box)}`} />}
          {isOffline ? (
            <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-surface-subtle text-ink-muted border border-surface-border whitespace-nowrap">
              Offline
            </span>
          ) : (
            <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-brand-green-light text-brand-green border border-brand-green-mid whitespace-nowrap">
              Online
            </span>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="card-divider" />

      {/* Metrics */}
      <div className="flex flex-col gap-1.5 sm:gap-2">
        {/* Last seen */}
        <div className="flex items-center justify-between gap-1">
          <span className="font-opensans text-ink-muted text-[10px] sm:text-xs flex-shrink-0">Zuletzt</span>
          <span className="font-mono text-[10px] sm:text-xs text-ink-secondary truncate text-right">
            {relativeTime(box.last_seen)}
          </span>
        </div>

        {/* Printer model – auf Mobile ausblenden */}
        {box.printer_model && (
          <div className="hidden sm:flex items-center justify-between gap-2">
            <span className="font-opensans text-ink-muted text-xs">Drucker</span>
            <span className="font-mono text-xs text-ink-secondary truncate max-w-[60%] text-right">
              {box.printer_model}
            </span>
          </div>
        )}

        {/* Printer status – nur wenn online */}
        {!isOffline && (
          <div className="flex items-center justify-between gap-1">
            <span className="font-opensans text-ink-muted text-[10px] sm:text-xs flex-shrink-0">Drucker</span>
            <div className="flex-shrink-0">
              <PrinterStatusBadge status={box.printer_status} printerError={box.printer_error} />
            </div>
          </div>
        )}

        {/* Printer error – nicht bei "Drucker nicht angeschlossen" */}
        {box.printer_error && !isOffline && box.printer_error !== 'Drucker nicht angeschlossen' && (
          <div className="rounded-lg bg-brand-red-light border border-brand-red-mid px-2 py-1.5">
            <p className="text-brand-red text-[10px] sm:text-xs leading-snug">
              ⚠ {box.printer_error}
            </p>
          </div>
        )}

        {/* Media size – auf Mobile ausblenden */}
        {box.media_size && !isOffline && (
          <div className="hidden sm:flex items-center justify-between gap-2">
            <span className="font-opensans text-ink-muted text-xs">Papierformat</span>
            <span className="font-mono text-xs text-ink-secondary">{box.media_size}</span>
          </div>
        )}

        {/* Prints remaining – NUR wenn online */}
        {!isOffline && box.prints_remaining !== null && (
          <PrintsBar
            remaining={box.prints_remaining}
            rollSize={box.roll_size ?? 400}
            threshold={20}
          />
        )}
      </div>

      {/* AnyDesk – nur sichtbar wenn online oder Admin */}
      {(isAdmin || effectiveAnydesk(box)) && (
        <>
          <div className="card-divider" />
          <AnydeskButton box={box} isAdmin={isAdmin} />
        </>
      )}

      {/* Footer: IP + firmware – auf Mobile ausblenden */}
      {(box.ip_address || box.firmware || box.version) && (
        <>
          <div className="card-divider hidden sm:block" />
          <div className="hidden sm:flex items-center justify-between gap-2 flex-wrap">
            {box.ip_address && (
              <span className="font-mono text-[10px] text-ink-faint">{box.ip_address}</span>
            )}
            <div className="flex items-center gap-2 ml-auto">
              {box.firmware && (
                <span className="font-mono text-[10px] text-ink-faint" title="Firmware">FW {box.firmware}</span>
              )}
              {box.version && (
                <span className="font-mono text-[10px] text-ink-faint">v{box.version}</span>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
